import type { RoleType } from './enums';
import type { AppMeta } from './app-meta.types';

// Matches scio2 Domains model — branding/white-label configuration.
// Stored on both MeResponse.brandingData and CompanyProfile.brandingData.
export interface BrandingData {
  _id?: string;
  email: string;
  isVerifiedEmail: boolean;
  emailFullname: string;
  accountId?: string;
  createdBy?: string;
  /** Custom domain (e.g. "branded.example.com") — takes precedence over subHost. */
  host?: string;
  /** Subdomain prefix used with the OptiSigns domain — resolves to `${subHost}.optisigns.net`. */
  subHost?: string;
  colorTheme: string;
  emailFrom: string;
  hideOptiSignsBrandedApps: boolean;
  hidePaymentHistoryPages: boolean;
  hideSubscriptionPages: boolean;
  removeOptiSignsBranding: boolean;
  hideCreateAccountButton: boolean;
  hideAccountMembersPage: boolean;
  hideExternalCommunications: boolean;
  customBrandCanvas: boolean;
  customBrandCanva: boolean;
  branding: boolean;
  isAllowGoogleLoginSocial: boolean;
  isAllowFBLoginSocial: boolean;
  isAllowMSLoginSocial: boolean;
  backgoundLogin: string;
  favicon: string;
  logoCompany: string;
  siteTitle: string;
  logo: string;
  templateCategories: string[];
  isWhiteLabel: boolean;
  isCustomDomain?: boolean;
  isInit?: boolean;
  styles?: string;
  stylesBox?: string;

  // ── Additional Branding Settings page fields (from getMyDomain) ──
  /** Login box position: ngTopCenter | ngTopLeft | ngTopRight | ngMiddleLeft | ngCenter | ngMiddleRight | ngBottonLeft | ngBottonRight | ngBottonCenter (scio1 typo "Botton" preserved). */
  loginBox?: string;
  loginBoxColor?: string;
  loginBoxPadding?: number;
  loginBoxOpacity?: number;
  loginBoxMarginLeftRight?: number;
  loginBoxMarginTopBottom?: number;

  /** scio1 typo preserved: "hideProofofPlay" not "hideProofOfPlay". */
  hideProofofPlay?: boolean;
  hideAuditLogging?: boolean;
  hideMonitorAndAlert?: boolean;

  /** Stored as String in legacy data; new writes are Boolean. See migrate-domain-active-host.ts. */
  isActiveHost?: boolean;
  /** Set when child host saves its own branding so parent updates stop overriding. */
  isNotParentUpdate?: boolean;
  /** True when account inherited branding from another account (host/subhost match). */
  isChildHost?: boolean;

  appManagement?: string[];
  appManagementForShareLinks?: string[];
  presenterUrl?: string;
}

/** Default branding values for non-white-label accounts (matches scio2 AuthService.branding getter) */
export const BRANDING_DEFAULTS: BrandingData = {
  email: 'support@optisign.com',
  isVerifiedEmail: true,
  emailFullname: 'OptiSigns',
  colorTheme: '#1abF7D',
  emailFrom: 'support@optisign.com',
  hideOptiSignsBrandedApps: false,
  hidePaymentHistoryPages: false,
  hideSubscriptionPages: false,
  removeOptiSignsBranding: false,
  hideCreateAccountButton: false,
  hideAccountMembersPage: false,
  hideExternalCommunications: false,
  customBrandCanvas: true,
  customBrandCanva: true,
  branding: true,
  isAllowGoogleLoginSocial: false,
  isAllowFBLoginSocial: false,
  isAllowMSLoginSocial: false,
  backgoundLogin: '',
  favicon: '/images/shortcut-icon.png',
  logoCompany: 'OS',
  siteTitle: 'OptiSigns',
  logo: '',
  templateCategories: [],
  isWhiteLabel: false,
};

/**
 * Resolve branding from raw API `brandingData`, matching scio2 `AuthService.branding` getter.
 *
 * - Non-white-label: returns BRANDING_DEFAULTS
 * - White-label: merges raw data over defaults, prepends S3 bucket URLs to
 *   logo/favicon/backgoundLogin/logoCompany
 *
 * @param raw — `brandingData` from MeResponse or CompanyProfile (may be partial)
 * @param awsBucketName — S3 bucket name (e.g. `VITE_AWS_BUCKET_NAME`)
 */
export function resolveBranding(
  raw: Partial<BrandingData> | undefined | null,
  awsBucketName = 'optisigns-assets',
): BrandingData {
  if (!raw?.isWhiteLabel) {
    return { ...BRANDING_DEFAULTS };
  }

  const s3Base = `https://${awsBucketName}.s3.amazonaws.com/`;
  const resolved: BrandingData = {
    ...BRANDING_DEFAULTS,
    ...raw,
    isWhiteLabel: true,
    isCustomDomain: true,
    isInit: true,
  };

  if (resolved.logo) {
    resolved.logo = s3Base + resolved.logo;
  }
  if (resolved.favicon) {
    resolved.favicon = s3Base + resolved.favicon;
  }
  resolved.backgoundLogin = resolved.backgoundLogin ? s3Base + resolved.backgoundLogin : '';
  resolved.logoCompany = resolved.logoCompany ? s3Base + resolved.logoCompany : 'OS';

  return resolved;
}

export interface MeTeam {
  _id: string;
  name: string;
  role: RoleType;
  permissions?: Record<string, boolean>;
  isTeamBilling?: boolean;
}

export interface MeResponse {
  _id: string;
  accountId: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  username: string;
  language: string;
  rootRole: RoleType;
  teams: MeTeam[];
  feature: Record<string, boolean>;
  featureData: Record<string, unknown>;
  brandingData: BrandingData;
  planFeatures: PlanFeature[];
  payment: Record<string, unknown>;
  accountInfo: Record<string, unknown>;
  userSettings: Record<string, unknown>;
  isSuperUser: boolean;
  /**
   * True when `me` was resolved via the share-link augmented `_getAuthFromDB`
   * path on the backend (anonymous viewer with `shareLinkObjectId` claim, OR a
   * real signed-in user whose `req.user` was overwritten by
   * `_augmentWithShareLinkContext`). `accountId`/`teams` reflect the SHARE
   * OWNER's context — NOT the caller's real account. Frontend uses this flag
   * to detect cross-team / cross-company share access without comparing
   * accountId (which is masked by augmentation).
   */
  isGuestUser?: boolean;
  ownerEmail: string;
  ownerName: string;
  passwordChangeRequire: boolean;
  accountStatus: boolean;
  fileCdnkeyData?: string;
  appMetas?: AppMeta[];
  appMetaGroups?: MeAppMetaGroup[];
  scioVersion?: number;
  inviteUser?: { email: string; fullname: string; role: string };
}

export interface MeAppMetaGroup {
  _id: string;
  title: string;
  description?: string;
  icon?: string;
  appTypes: string[];
  order?: number;
  isShowWelcome?: boolean;
  orderWelcome?: number;
  isFree?: boolean;
  isSkipSelectApp?: boolean;
  settings?: {
    onlyShowDescription?: boolean;
    redirectLabel?: string;
    redirectURL?: string;
  };
  appContent?: string;
}

export interface PlanFeature {
  _id: string;
  name: string;
  features: { name: string; enable: boolean }[];
}

export interface UserSettings {
  _id: string;
  isShowWelcomeTips: boolean;
  isShowOrderAlertStick: boolean;
  onboardFormSkip: boolean;
  onboardFormSubmitted: boolean;
  firstGetStarted: boolean;
  firstGetStartedDate: string;
  themeMode: string;
  dateFormat: string;
  timeFormat: string;
  numberFormat: string;
  scaleTemperature: string;
  windSpeedUnit: string;
  defaultValueType: string;
  assetIsDupClickMode?: boolean;
  isHideBannerPromotionForPaidUser: boolean;
  // Analytics first-event flags (persisted via updateUserSettingsAuth)
  firstAssetCreated?: boolean | null;
  firstPlaylistCreated?: boolean | null;
  firstScheduleCreated?: boolean | null;
  firstTeamMemberInvited?: boolean | null;
  firstTemplateUsed?: boolean | null;
  firstDeviceOrdered?: boolean | null;
  needAnalyticFirst?: boolean;
}
